import React from 'react'
import { useCurrentFrame, useVideoConfig, interpolate } from 'remotion'
import { COLORS, FONTS, GLOW, GLASS } from '../shared/theme'
import { fadeIn, entrance, staggerDelay, breathe, SPRINGS } from '../shared/animations'
import SceneWrapper from '../shared/SceneWrapper'

/**
 * CodeBlock — Código con syntax highlighting y reveal línea a línea.
 *
 * params: { lines: [string], language, title }
 */

const KEYWORD_SET = new Set([
  'def',
  'class',
  'import',
  'from',
  'return',
  'if',
  'elif',
  'else',
  'for',
  'while',
  'in',
  'not',
  'and',
  'or',
  'True',
  'False',
  'None',
  'const',
  'let',
  'var',
  'function',
  'async',
  'await',
  'export',
  'default',
  'new',
  'this',
  'try',
  'catch',
  'finally',
  'throw',
  'public',
  'private',
  'protected',
  'static',
  'interface',
  'type',
  'extends',
  'implements',
  'void',
  'null',
  'undefined',
  'print',
  'console',
  'log',
  'int',
  'str',
  'bool',
  'float',
  'list',
  'dict',
  'set',
  'pass',
  'break',
  'continue',
  'yield',
  'lambda',
  'with',
  'as',
  'raise',
  'except',
  'enum',
  'struct',
  'fn',
  'mut',
  'use',
  'mod',
  'pub',
  'impl',
  'self',
  'match',
  'Some',
  'None',
  'Ok',
  'Err',
])

function tokenizeLine(line) {
  const stripped = line.trim()
  if (
    stripped.startsWith('#') ||
    stripped.startsWith('//') ||
    stripped.startsWith('*') ||
    stripped.startsWith('/*')
  ) {
    return [{ text: line, type: 'comment' }]
  }
  const regex = /("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|`(?:[^`\\]|\\.)*`|\b\w+\b|\s+|[^\w\s])/g
  const tokens = []
  let m
  while ((m = regex.exec(line)) !== null) {
    const text = m[0]
    let type = 'normal'
    if (/^["'`]/.test(text)) type = 'string'
    else if (/^\d+\.?\d*$/.test(text)) type = 'number'
    else if (KEYWORD_SET.has(text)) type = 'keyword'
    else if (/^\s+$/.test(text)) type = 'whitespace'
    else if (/^[(){}[\]:,.<>=!+\-*/&|^~%@]+$/.test(text)) type = 'operator'
    tokens.push({ text, type })
  }
  return tokens
}

const TOKEN_COLORS = {
  keyword: COLORS.accent.purple,
  string: COLORS.accent.orange,
  comment: COLORS.text.muted,
  number: COLORS.accent.teal,
  operator: '#8E8E93',
  whitespace: 'transparent',
  normal: COLORS.text.primary,
}

export default function CodeBlock({ params = {}, artDirection = {} }) {
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const { lines = [], language = 'code', title = '' } = params

  const count = Math.min(lines.length, 12)
  const staggerFrames = 8
  const startDelay = 12
  const fontSize = count <= 8 ? 22 : count <= 10 ? 19 : 16
  const lineHeight = fontSize * 1.75

  const containerOpacity = fadeIn(frame, 0, 18)
  const containerScale = entrance(frame, fps, 0, SPRINGS.gentle)
  const headerOpacity = fadeIn(frame, 6, 15)

  // Which line is currently being "typed" (last visible line)
  const activeIdx = Math.min(Math.floor(Math.max(0, frame - startDelay) / staggerFrames), count - 1)
  const cursorBlink = Math.floor(frame / 7) % 2 === 0

  const containerPadding = 40
  const lineNumWidth = 44
  const cardWidth = Math.min(1360, 1920 * 0.72)
  const cardHeight = count * lineHeight + containerPadding * 2 + 52 // 52 for header

  return (
    <SceneWrapper accent={COLORS.accent.teal} texture={artDirection?.texture}>
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Title above card */}
        {title ? (
          <div
            style={{
              opacity: headerOpacity,
              fontFamily: FONTS.heading,
              fontSize: 26,
              fontWeight: 600,
              color: COLORS.text.muted,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              marginBottom: 24,
            }}
          >
            {title}
          </div>
        ) : null}

        {/* Code card */}
        <div
          style={{
            opacity: containerOpacity,
            transform: `scale(${containerScale})`,
            width: cardWidth,
            borderRadius: 20,
            ...GLASS.card,
            border: `1px solid ${COLORS.accent.teal}20`,
            boxShadow: GLOW.md(COLORS.accent.teal),
            overflow: 'hidden',
          }}
        >
          {/* Editor header bar */}
          <div
            style={{
              height: 44,
              background: 'rgba(255,255,255,0.03)',
              borderBottom: `1px solid rgba(255,255,255,0.06)`,
              display: 'flex',
              alignItems: 'center',
              padding: '0 18px',
              gap: 8,
            }}
          >
            {/* Traffic lights */}
            {['#FF5F57', '#FEBC2E', '#28C840'].map((c, i) => (
              <div
                key={i}
                style={{ width: 12, height: 12, borderRadius: '50%', background: c, opacity: 0.8 }}
              />
            ))}
            <div
              style={{
                marginLeft: 12,
                fontFamily: FONTS.mono,
                fontSize: 13,
                color: COLORS.text.muted,
                letterSpacing: '0.05em',
              }}
            >
              {language}
            </div>
            {/* Language badge */}
            <div
              style={{
                marginLeft: 'auto',
                fontFamily: FONTS.mono,
                fontSize: 12,
                color: COLORS.accent.teal,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
              }}
            >
              {language}
            </div>
          </div>

          {/* Code lines */}
          <div
            style={{
              padding: `${containerPadding * 0.6}px ${containerPadding}px`,
              display: 'flex',
              gap: 0,
            }}
          >
            {/* Line numbers */}
            <div
              style={{
                width: lineNumWidth,
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 0,
                borderRight: `1px solid rgba(255,255,255,0.05)`,
                marginRight: 24,
                paddingRight: 12,
              }}
            >
              {lines.slice(0, count).map((_, i) => {
                const delay = startDelay + staggerDelay(i, staggerFrames)
                const lineOpacity = fadeIn(frame, delay, 6)
                return (
                  <div
                    key={i}
                    style={{
                      height: lineHeight,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      opacity: lineOpacity * 0.3,
                      fontFamily: FONTS.mono,
                      fontSize: fontSize - 2,
                      color: COLORS.text.muted,
                      userSelect: 'none',
                    }}
                  >
                    {i + 1}
                  </div>
                )
              })}
            </div>

            {/* Code content */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              {lines.slice(0, count).map((line, i) => {
                const delay = startDelay + staggerDelay(i, staggerFrames)
                const lineOpacity = fadeIn(frame, delay, 8)
                const lineX = interpolate(frame, [delay, delay + 12], [-20, 0], {
                  extrapolateLeft: 'clamp',
                  extrapolateRight: 'clamp',
                })
                const isActive = i === activeIdx
                const tokens = tokenizeLine(line)

                return (
                  <div
                    key={i}
                    style={{
                      height: lineHeight,
                      display: 'flex',
                      alignItems: 'center',
                      opacity: lineOpacity,
                      transform: `translateX(${lineX}px)`,
                      background: isActive ? 'rgba(90,200,250,0.04)' : 'transparent',
                      borderRadius: 4,
                      paddingLeft: 4,
                    }}
                  >
                    {tokens.map((token, j) => (
                      <span
                        key={j}
                        style={{
                          fontFamily: FONTS.mono,
                          fontSize,
                          color: TOKEN_COLORS[token.type] || COLORS.text.primary,
                          whiteSpace: 'pre',
                          fontStyle: token.type === 'comment' ? 'italic' : 'normal',
                        }}
                      >
                        {token.text}
                      </span>
                    ))}
                    {/* Blinking cursor on active line */}
                    {isActive && cursorBlink && (
                      <span
                        style={{
                          display: 'inline-block',
                          width: 2,
                          height: fontSize + 2,
                          background: COLORS.accent.teal,
                          marginLeft: 2,
                          boxShadow: GLOW.sm(COLORS.accent.teal),
                          verticalAlign: 'middle',
                        }}
                      />
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </SceneWrapper>
  )
}
